import classNames from 'classnames';
import React, { JSX, ReactNode } from 'react';
import BrokenBulb from '../../assets/broken-bulb.svg';
import BrokenBulbWhite from '../../assets/broken-bulb-white.svg';
import StorroLogo from 'assets/storro-logo-and-text-light.svg';
import AgencyDriveLogo from 'assets/agency-drive-logo.svg';
import Button, { ButtonVariant } from '../Button';
import useTheme from 'hooks/UseTheme';
import { useTranslation } from 'react-i18next';

interface Props {
  title: string;
  text: string;
  showGoHomeButton?: boolean;
  onGoHomeButtonClick?: () => void;
  external?: boolean;
  eventId?: string;
  children?: ReactNode;
}

export default function PageError({ title, text, showGoHomeButton, external, onGoHomeButtonClick, eventId, children }: Props): JSX.Element {
  const { companyName, isStorroTheme } = useTheme();
  const { t } = useTranslation();

  return (
    <div className='relative'>
      <div
        className={classNames('min-h-screen w-full flex items-center justify-center', {
          'bg-white': !external,
          'bg-[#263641]': external,
        })}
      >
        <div className='flex flex-col md:flex-row gap-x-10 items-center p-6'>
          <div className='relative md:w-64 w-32 flex justify-center '>
            <div className='bg-primary/10 rounded-full md:w-64 md:h-64 w-52 h-52 absolute' />
            <img src={external ? BrokenBulbWhite : BrokenBulb} alt='Broken Bulb' />
          </div>
          <div
            className={classNames('text-center md:text-left', {
              'text-gray-200': external,
            })}
          >
            <h1
              className={classNames('text-8xl', {
                'text-primary font-thin': !external,
                'text-gray-200 font-semibold': external,
              })}
            >
              {title}
            </h1>
            <div className='text-xl mb-4 max-w-[500px]'>{text}</div>
            {eventId && (
              <p className='text-xs text-gray-400 mb-4'>
                {t('error-event-id', 'Event ID')}: <span className='font-mono'>{eventId}</span>
              </p>
            )}
            {showGoHomeButton && (
              <Button variant={ButtonVariant.Primary} onClick={onGoHomeButtonClick}>
                {t('go-home', 'Go home')}
              </Button>
            )}
            {children}
          </div>
        </div>
      </div>

      {external && (
        <div className='absolute bottom-4 flex w-full justify-center'>
          <div className='flex items-center gap-x-2 justify-center opacity-75'>
            <img className='hidden xs:block' src={isStorroTheme ? StorroLogo : AgencyDriveLogo} alt='logo' width='75' />
            <span className='text-gray-200 text-xs lg:text-sm'>
              {t('copyright', 'Copyright')} © {new Date().getFullYear()} {companyName} {'B.V.'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
